import type { User } from "services/users/model";
import { ASSETS_FOLDER_NAME, EDITOR_FILE_NAME, EXERCISES_FOLDER_ROOT } from "./utils";
import type { Tag } from "services/tags/model";
import type { Level } from "services/levels";

type S3Object = {
  key: string;
  prefix: string;
  name: string;
  isPrivate: boolean;
  libraryObject?: LibraryObject;
};

export const FolderRootType = {
  ADMIN_CONTENT: "admin-content",
  EXERCISE: "exercise",
} as const;

export type FolderRootType = (typeof FolderRootType)[keyof typeof FolderRootType];

export function getExercisesRootFolder(sessionUser: User) {
  if (!sessionUser.instructor) {
    throw new Error("The user is not an instructor");
  }

  return `${EXERCISES_FOLDER_ROOT}/${sessionUser.instructor.id}/`;
}

export type Folder = S3Object;

export type InputFieldConfiguration = {
  label: string;
  required: boolean;
  maxLength?: number;
  isVisible?: boolean;
  type?: string;
};

export class FolderUI {
  label: string = "Folder";
  uiFormFields: Record<string, InputFieldConfiguration> = {
    name: {
      label: "Name",
      required: true,
      maxLength: 128,
      isVisible: true,
    },
    description: {
      label: "Description",
      required: false,
      maxLength: 512,
      isVisible: true,
    },
    author: {
      label: "Author",
      required: false,
      maxLength: 128,
      isVisible: true,
    },
    preview: {
      type: "file",
      label: "Icon (Square)",
      required: false,
      isVisible: true,
    },
    preview_vertical: {
      type: "file",
      label: "Icon (Vertical)",
      required: false,
      isVisible: true,
    },
    preview_horizontal: {
      type: "file",
      label: "Icon (Horizontal)",
      required: false,
      isVisible: true,
    },
    order: {
      type: "number",
      label: "Order",
      required: false,
      isVisible: true,
    },
    length: {
      type: "number",
      label: "Length",
      required: false,
      isVisible: true,
    },
    subtype: {
      label: "Sub Type",
      required: false,
      isVisible: true,
    },
    readings: {
      type: "number",
      label: "Readings",
      required: false,
      isVisible: true,
    },
    videos: {
      type: "number",
      label: "Videos",
      required: false,
      isVisible: true,
    },
  };
  isAddContentButtonVisible: boolean = false;
  nextLevelFolderUI: FolderUI = { label: "Folder" } as FolderUI;
}

export class ContentSourceFolderUI extends FolderUI {
  constructor() {
    super();
    this.label = "Source";
  }
}

export class ExerciseFolderUI extends FolderUI {
  constructor() {
    super();
    this.label = "Folder";
    this.uiFormFields.description.required = false;
    this.uiFormFields.description.isVisible = false;
    this.uiFormFields.author.required = false;
    this.uiFormFields.author.isVisible = false;
    this.uiFormFields.preview.required = false;
    this.uiFormFields.preview.isVisible = false;
    this.uiFormFields.preview_vertical.isVisible = false;
    this.uiFormFields.preview_horizontal.isVisible = false;
    this.uiFormFields.order.isVisible = false;
  }
}

function stripTrailingSlash(key: string) {
  return key.endsWith("/") ? key.slice(0, -1) : key;
}

export function getFolderUIClass(key: string) {
  let folderUIClass = new FolderUI();
  folderUIClass.nextLevelFolderUI = new FolderUI();

  if (key === "") {
    folderUIClass.nextLevelFolderUI = new ContentSourceFolderUI();
  } else if (key.includes("/")) {
    const normalizedKey = stripTrailingSlash(key);
    const firstParent = normalizedKey.split("/")[0];

    if (firstParent.toLowerCase().includes(EXERCISES_FOLDER_ROOT)) {
      folderUIClass = new ExerciseFolderUI();
      folderUIClass.nextLevelFolderUI = new ExerciseFolderUI();
    }
  }

  return folderUIClass;
}

export type File = S3Object & {
  lastModified?: Date;
};

export type FolderContents = { folders: Folder[]; files: File[] };

export const LibraryObjectType = {
  COURSE: "course",
  LESSON: "lesson",
  FOLDER: "folder",
} as const;

export type LibraryObjectType =
  (typeof LibraryObjectType)[keyof typeof LibraryObjectType];

export class LibraryObject {
  id: string = "";
  key: string = "";
  name: string = "";
  description?: string;
  preview_url?: string;
  preview_vertical_url?: string;
  preview_horizontal_url?: string;
  preview?: globalThis.File | File;
  preview_vertical?: globalThis.File | File;
  preview_horizontal?: globalThis.File | File;
  author?: string;
  order?: number;
  parent?: LibraryObject | ParentId;
  length?: number;
  subtype?: string;
  readings?: number;
  videos?: number;
  tags?: Tag[];
  type?: LibraryObjectType;
  icon_url?: string;
  tier?: string;
  progress?: {
    progress: number;
    completed: Array<{ id: string; length?: number }>;
    remaining: Array<{ id: string; length?: number }>;
  } | null;
  children?: {
    lessons: number;
    "case-studies": number;
    projects: number;
  };
  is_favorite?: boolean;

  clone() {
    const object = new LibraryObject();
    object.id = this.id;
    object.key = this.key;
    object.name = this.name;
    object.description = this.description;
    object.preview_url = this.preview_url;
    object.preview_vertical_url = this.preview_vertical_url;
    object.preview_horizontal_url = this.preview_horizontal_url;
    object.preview = this.preview;
    object.preview_vertical = this.preview_vertical;
    object.preview_horizontal = this.preview_horizontal;
    object.author = this.author;
    object.order = this.order;
    object.parent =
      this.parent && this.parent instanceof LibraryObject
        ? this.parent.clone()
        : this.parent && { ...this.parent };
    object.length = this.length;
    object.subtype = this.subtype;
    object.readings = this.readings;
    object.videos = this.videos;
    object.tags = this.tags;
    object.type = this.type;
    object.icon_url = this.icon_url;
    object.tier = this.tier;
    object.progress = this.progress;
    object.children = this.children;
    object.is_favorite = this.is_favorite;

    return object;
  }
}

type EditorHierarchy = {
  id: string;
  name: string;
  type: "object" | "version";
  editor_hierarchy: EditorHierarchy | null;
};

export type AdminLibraryObject = LibraryObject & { editor_hierarchy: EditorHierarchy };

export class ParentId {
  id: string = "";
}

export type LibraryVersion = {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  preview_url?: string;
  preview_vertical_url?: string;
  preview_horizontal_url?: string;
  preview?: globalThis.File | File;
  preview_vertical?: globalThis.File | File;
  preview_horizontal?: globalThis.File | File;
  author?: string | null;
  order?: number | null;
  editor_hierarchy: EditorHierarchy;
  length?: number | null;
  subtype?: string | null;
  readings?: number | null;
  videos?: number | null;
  type: LibraryObjectType;
  tags: Tag[];
  original?: { id: string };
  icon_url?: string;
  tier?: string | null;
  // Background S3 copy state (admin only). Null for blank drafts (nothing copied).
  content_copy_status?: "queued" | "running" | "succeeded" | "failed" | null;
  content_copy_error?: string | null;
};

// Mirrors the backend CreateVersionRequest (srv-async library/admin). All fields optional:
// metadata overrides the source, original_*/keep_original_link select the copy source, and the
// parent_* fields are tri-state (omitted = inherit from source, null = move to root, id = re-parent).
export type CreateLibraryVersionRequest = {
  name?: string;
  description?: string | null;
  author?: string | null;
  order?: number | null;
  length?: number | null;
  subtype?: string | null;
  readings?: number | null;
  videos?: number | null;
  type?: LibraryObjectType;
  tier?: string | null;
  original_id?: string;
  original_version_id?: string;
  keep_original_link?: boolean;
  parent_library_object_id?: string | null;
  parent_library_version_id?: string | null;
};

// Mirrors the backend PatchVersionSchema: every metadata field plus the tri-state
// parent fields (null = move to root) used by the move flow.
export type PatchVersionRequest = Partial<LibraryVersion> & {
  parent_library_object_id?: string | null;
  parent_library_version_id?: string | null;
};

export type LibraryDraftSpaceResponse = {
  library_objects: AdminLibraryObject[];
  library_versions: LibraryVersion[];
  parent_library_object?: AdminLibraryObject;
  parent_library_version?: LibraryVersion;
};

export type UploadLibraryResourceResponse = {
  list: Array<{ name: string; upload_url: string }>;
};

export type IEntry = {
  id: string;
  name: string;
  description: string;
  highlights: {
    content: string;
  };
  type: string;
  author: string;
  is_favorite: boolean;
  preview_url?: string;
};

export type IOnClose = {
  refresh: boolean;
};

export type Workshop = {
  id: string;
  name: string;
  description: string;
  length: number;
  scheduled: string;
  level: Level;
  registration_link: string;
  preview_url: string;
};

export type WorkshopListResponse = {
  list: Workshop[];
  total: number;
};

export { ASSETS_FOLDER_NAME, EDITOR_FILE_NAME, EXERCISES_FOLDER_ROOT };
